import VideoPlayer from "@dana/Components/VideoPlayer";
import PrevArrowIcon from "@dana/Icons/PrevArrowIcon";
import Search from "@dana/MobiusOverrides/Icons/Search";
import Youtube from "@dana/MobiusOverrides/Icons/Youtube";
import { themeBackgrounds, themeColors } from "@dana/MobiusOverrides/styles/colors";
import { EZIDAttributeFilter } from "@dana/Types/EZIDAttributeFilter";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Img, { ImgProps } from "@insite/mobius/Img";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextField from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React, { useEffect, useState } from "react";
import { css } from "styled-components";

interface LengthSelectorScreenProps {
    video?: LinkFieldValue;
    caption?: LinkFieldValue;
    backLabel?: string;
    slideTitle?: string;
    subText?: string;
    description?: string;
    additionalText?: string;
    image?: string;
    imageAltText?: string;
    leftFacetLabel?: string;
    rightFacetLabel?: string;
    leftNote?: string;
    rightNote?: string;
    watchVideoLabel?: string;
    stepLabel?: string;
    nextLabel?: string;
    step?: number;
    readyshaftCategoryId?: string;
    currentAttributeFilters?: EZIDAttributeFilter[];
    attributeNameInMillimeters: string;
    attributeNameInInches: string;
    onProceedToNextScreen: (newAttributeFilters?: EZIDAttributeFilter[]) => void;
    onProceedToPreviousScreen: (newAttributeFilters?: EZIDAttributeFilter[]) => void;
}

export interface LengthSelectorScreenStyles {
    wrapper?: InjectableCss;
    backButton?: ButtonPresentationProps;
    heading?: TypographyPresentationProps;
    subText?: TypographyPresentationProps;
    description?: TypographyPresentationProps;
    contentWrapper?: InjectableCss;
    twoColWrapper?: InjectableCss;
    watchButtonWrapper?: InjectableCss;
    actionWrapper?: InjectableCss;
    image?: ImgProps;
    checkBoxGroupWrapper?: InjectableCss;
    videoModal?: ModalPresentationProps;
    watchVideoButton?: ButtonPresentationProps;
}

export const styles: LengthSelectorScreenStyles = {
    wrapper: {
        css: css`
                display: flex;
                flex-direction: column;
                align-items: center;
                width: 90%;
                gap: 24px;
                margin: 18px auto 0 auto;
                            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                                                        width: 100%;
                                                    `,
                        css`
                                                        width: 100%;
                                                    `,
                        css`
                                                        width: 100%;
                                                    `,
                        null,
                        null,
                    ],
                    "max",
                )
            }
            `,
    },
    backButton: {
        variant: "tertiary",
        css: css`
                align-self: flex-start;
                text-align: left;
            `,
        icon: {
            src: PrevArrowIcon,
            position: "left",
        },
    },
    heading: {
        variant: "h3",
        css: css`
                margin-bottom: 0;
                text-align: center;
            `,
    },
    subText: {
        variant: "h4",
        css: css`
                text-align: center;
                margin-bottom: 0;
            `,
    },
    description: {
        variant: "h5",
        transform: "none",
        color: themeColors.primary.richBlack,
        css: css`
                    letter-spacing: inherit;
                    text-align: center;
                    margin-bottom: 0;
                `,
    },
    contentWrapper: {
        css: css`
                display: flex;
                flex-direction: column;
                align-items: center;
                width: 100%;
                border: 1px solid ${themeBackgrounds.lightGray};
                border-radius: 4px;
                padding: 16px;
                box-sizing: border-box;
    
                & > *:not(:last-child) {
                    border-bottom: 1px solid ${themeBackgrounds.lightGray};
                    padding-bottom: 16px;
                    margin-bottom: 16px;
                }
            `,
    },
    image: {
        css: css`
            width: auto;
            max-width: 100%;
            max-height: 300px;
            height: auto;
            display: block;
            object-fit: contain;
            `,
    },
    twoColWrapper: {
        css: css`
                display: flex;
                flex-direction: row;
                gap: 16px;
                width: 100%;
                & > * {
                    flex: 1 1 0;
                    min-width: 0;
                }
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-direction: column;
                            align-items: stretch;
                            gap: 12px;
                            & > * {
                                flex: unset;
                            }
                        `,
                        css`
                            flex-direction: column;
                            align-items: stretch;
                            gap: 14px;
                            & > * {
                                flex: unset;
                            }
                        `,
                        css`
                            flex-direction: column;
                            align-items: stretch;
                            gap: 14px;
                            & > * {
                                flex: unset;
                            }
                        `,
                        null,
                        null,
                    ],
                    "max",
                )
            }
            `,
    },
    actionWrapper: {
        css: css`
                display: flex;
                flex-direction: row;
                justify-content: space-between;
                width: 100%;
                gap: 16px;
            `,
    },
    checkBoxGroupWrapper: {
        css: css`
                max-height: 300px;
                overflow-y: auto;
            `,
    },
    watchButtonWrapper: {
        css: css`
                    border-bottom: none !important;
                `,
    },
    videoModal: {
        cssOverrides: {
            modalContent: css`
                    video {
                        width: 100%;
                        height: auto;
                        max-height: 70vh;
                        display: block;
                    }
                `,
        },
        sizeVariant: "large",
    },
    watchVideoButton: {
        variant: "primary",
        icon: {
            position: "left",
            src: () => <Youtube />
        },
    },
};

const LengthSelectorScreen: React.FC<LengthSelectorScreenProps> = ({
    video,
    caption,
    backLabel,
    slideTitle,
    subText,
    description,
    additionalText,
    image,
    imageAltText,
    leftFacetLabel,
    rightFacetLabel,
    leftNote,
    rightNote,
    watchVideoLabel,
    stepLabel,
    nextLabel,
    step,
    readyshaftCategoryId,
    currentAttributeFilters,
    attributeNameInMillimeters,
    attributeNameInInches,
    onProceedToNextScreen,
    onProceedToPreviousScreen,
}) => {
    const [showLoadingOverlay, setShowLoadingOverlay] = React.useState(true);

    const [videoModalIsOpen, setVideoModalIsOpen] = useState(false);
    const videoLink = video ? useGetLink(video) : null;
    const captionLink = caption ? useGetLink(caption) : null;

    const [lengthInMmData, setLengthInMmData] = React.useState<EZIDAttributeFilter[]>([]);
    const [selectedLengthInMm, setSelectedLengthInMm] = useState<string[]>([]);
    const [lengthInMmSearchText, setLengthInMmSearchText] = useState("");
    const searchedLengthInMmData = lengthInMmData.filter(length =>
        length.attributeValueName.toLowerCase().includes(lengthInMmSearchText.toLowerCase())
    );

    const [lengthInInchesData, setLengthInInchesData] = React.useState<EZIDAttributeFilter[]>([]);
    const [selectedLengthInInches, setSelectedLengthInInches] = useState<string[]>([]);
    const [lengthInInchesSearchText, setLengthInInchesSearchText] = useState("");
    const searchedLengthInInchesData = lengthInInchesData.filter(length =>
        length.attributeValueName.toLowerCase().includes(lengthInInchesSearchText.toLowerCase())
    );

    useEffect(() => {
        const searchProducts = async (isMM: boolean) => {
            try {
                const result = await fetch("/api/v1/ezid/getProductFacets", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        categoryId: readyshaftCategoryId,
                        // Exclude the current measurement from the initial filters to get all measurement facets
                        attributeValueIds: currentAttributeFilters
                            ?.filter(f => f.attributeTypeName?.toLowerCase() !== attributeNameInMillimeters.toLowerCase() && f.attributeTypeName?.toLowerCase() !== attributeNameInInches.toLowerCase())
                            ?.map(filter => filter.attributeValueId) || [],
                        groupName: isMM ? attributeNameInMillimeters : attributeNameInInches,
                    }),
                });

                if (!result.ok) {
                    throw new Error(result.statusText);
                }

                const data = await result.json();
                populateCheckboxes(data, isMM);

            } catch (error) {
                console.error("Error fetching products:", error);
            } finally {
                setShowLoadingOverlay(false);
            }
        };

        const handleExistingFilters = () => {
            if (currentAttributeFilters) {
                const mmFilters = currentAttributeFilters.filter(
                    filter => filter.attributeTypeName?.toLowerCase() === attributeNameInMillimeters.toLowerCase()
                );
                if (mmFilters.length > 0) {
                    setSelectedLengthInMm(mmFilters.map(filter => filter.attributeValueId));
                } else {
                    const inchesFilters = currentAttributeFilters.filter(
                        filter => filter.attributeTypeName?.toLowerCase() === attributeNameInInches.toLowerCase()
                    );
                    if (inchesFilters.length > 0) {
                        setSelectedLengthInInches(inchesFilters.map(filter => filter.attributeValueId));
                    }
                }
            }
        }

        handleExistingFilters();
        searchProducts(true);
        searchProducts(false);
    }, []);

    const populateCheckboxes = (apiData: any, isMM: boolean) => {
        if (apiData) {
            // Sort by attributeValueName as float
            const sortedData = [...apiData].sort((a: EZIDAttributeFilter, b: EZIDAttributeFilter) => {
                const aValue = parseFloat(a.attributeValueName);
                const bValue = parseFloat(b.attributeValueName);
                return aValue - bValue;
            });

            if (isMM) {
                const facets = sortedData.filter(
                    (facet: { attributeTypeName: string }) => facet.attributeTypeName.toLowerCase() === attributeNameInMillimeters.toLowerCase());
                if (facets) {
                    setLengthInMmData(facets);
                }
            } else {
                const facets = sortedData.filter(
                    (facet: { attributeTypeName: string }) => facet.attributeTypeName.toLowerCase() === attributeNameInInches.toLowerCase());
                if (facets) {
                    setLengthInInchesData(facets);
                }
            }
        }
    };

    const onNextButtonClick = () => {
        // Remove all old filters with attributeTypeName = attributeNameInMillimeters and attributeNameInInches
        const noLengthAttrFilters =
            currentAttributeFilters
                ?.filter(f => f.attributeTypeName?.toLowerCase() !== attributeNameInMillimeters.toLowerCase() && f.attributeTypeName?.toLowerCase() !== attributeNameInInches.toLowerCase()) || [];

        // Add selected data
        const selectedDataInMmFilters = searchedLengthInMmData
            .filter(length => selectedLengthInMm.includes(length.attributeValueId))
            .map(length => ({
                attributeTypeId: length.attributeTypeId,
                attributeTypeName: length.attributeTypeName,
                attributeValueId: length.attributeValueId,
                attributeValueName: length.attributeValueName,
                count: length.count,
            }));

        const selectedDataInInchesFilters = searchedLengthInInchesData
            .filter(length => selectedLengthInInches.includes(length.attributeValueId))
            .map(length => ({
                attributeTypeId: length.attributeTypeId,
                attributeTypeName: length.attributeTypeName,
                attributeValueId: length.attributeValueId,
                attributeValueName: length.attributeValueName,
                count: length.count,
            }));

        // Call onProceedToNextScreen with the new filters
        const newAttributeFilters = [...noLengthAttrFilters, ...selectedDataInMmFilters, ...selectedDataInInchesFilters];
        onProceedToNextScreen(newAttributeFilters);
    }

    const onBackButtonClick = () => {
        // Remove all old filters with attributeTypeName = attributeNameInMillimeters and attributeNameInInches
        const noLengthAttrFilters =
            currentAttributeFilters
                ?.filter(f => f.attributeTypeName?.toLowerCase() !== attributeNameInMillimeters.toLowerCase() && f.attributeTypeName?.toLowerCase() !== attributeNameInInches.toLowerCase()) || [];
        onProceedToPreviousScreen(noLengthAttrFilters);
    }

    const videoModalCloseHandler = () => {
        setVideoModalIsOpen(false);
    };

    return (
        <LoadingOverlay
            loading={showLoadingOverlay}
            css={css`
                width: 100%;
            `}
        >
            <StyledWrapper {...styles.wrapper} className="wrapper">
                <Button
                    {...styles.backButton}
                    onClick={onBackButtonClick}>
                    {backLabel}
                </Button>
                <Typography {...styles.heading} as="h3">
                    {slideTitle}
                </Typography>
                <Typography {...styles.subText} as="h4">
                    {subText}
                </Typography>
                <Typography {...styles.description} as="h5">
                    {description}
                </Typography>
                {additionalText && (
                    <Typography {...styles.subText} as="h4">
                        {additionalText}
                    </Typography>
                )}
                <StyledWrapper {...styles.contentWrapper}>
                    <StyledWrapper {...styles.twoColWrapper}>
                        {image && (
                            <Img src={image} altText={imageAltText || ''} {...styles.image} />
                        )}
                        <StyledWrapper {...styles.twoColWrapper}>
                            <StyledWrapper {...styles.checkBoxGroupWrapper}>
                                <Typography as="b">{leftFacetLabel} ({lengthInMmData.length})</Typography>
                                <TextField
                                    disabled={selectedLengthInInches.length > 0}
                                    className="search-text-field"
                                    id="mmTextField"
                                    value={lengthInMmSearchText}
                                    onChange={e => {
                                        setLengthInMmSearchText(e.target.value);
                                    }}
                                    iconProps={{ src: Search }}
                                />
                                <CheckboxGroup disabled={selectedLengthInInches.length > 0}>
                                    {searchedLengthInMmData.map((length) => (
                                        <Checkbox
                                            disabled={selectedLengthInInches.length > 0}
                                            checked={selectedLengthInMm.includes(length.attributeValueId)}
                                            key={length.attributeValueId}
                                            onChange={() => {
                                                setSelectedLengthInMm((prev) => {
                                                    if (prev.includes(length.attributeValueId)) {
                                                        return prev.filter(id => id !== length.attributeValueId);
                                                    }
                                                    return [...prev, length.attributeValueId];

                                                });
                                            }}
                                        >
                                            {`${length.attributeValueName} (${length.count})`}
                                        </Checkbox>
                                    ))}
                                </CheckboxGroup>
                            </StyledWrapper>
                            <StyledWrapper {...styles.checkBoxGroupWrapper}>
                                <Typography as="b">{rightFacetLabel} ({lengthInInchesData.length})</Typography>
                                <TextField
                                    disabled={selectedLengthInMm.length > 0}
                                    className="search-text-field"
                                    id="inTextField"
                                    value={lengthInInchesSearchText}
                                    onChange={e => {
                                        setLengthInInchesSearchText(e.target.value);
                                    }}
                                    iconProps={{ src: Search }}
                                />
                                <CheckboxGroup disabled={selectedLengthInMm.length > 0}>
                                    {searchedLengthInInchesData.map((length) => (
                                        <Checkbox
                                            disabled={selectedLengthInMm.length > 0}
                                            checked={selectedLengthInInches.includes(length.attributeValueId)}
                                            key={length.attributeValueId}
                                            onChange={() => {
                                                setSelectedLengthInInches((prev) => {
                                                    if (prev.includes(length.attributeValueId)) {
                                                        return prev.filter(id => id !== length.attributeValueId);
                                                    }
                                                    return [...prev, length.attributeValueId];

                                                });
                                            }}
                                        >
                                            {`${length.attributeValueName} (${length.count})`}
                                        </Checkbox>
                                    ))}
                                </CheckboxGroup>
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>

                    <StyledWrapper {...styles.twoColWrapper}>
                        {leftNote && (
                            <Typography className="text-body-lg">
                                {parse(leftNote, parserOptions)}
                            </Typography>
                        )}
                        {rightNote && (
                            <Typography className="text-body-lg">
                                {parse(rightNote, parserOptions)}
                            </Typography>
                        )}
                    </StyledWrapper>
                    {videoLink?.url && (
                        <StyledWrapper {...styles.watchButtonWrapper}>
                            <Button {...styles.watchVideoButton} onClick={() => setVideoModalIsOpen(true)}>
                                {watchVideoLabel}
                            </Button>
                        </StyledWrapper>
                    )}
                    <StyledWrapper {...styles.actionWrapper}>
                        <Typography as="b">{stepLabel} {step}</Typography>
                        <Button variant="primary" onClick={onNextButtonClick} disabled={selectedLengthInMm.length === 0 && selectedLengthInInches.length === 0}>
                            {nextLabel}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            <Modal
                {...styles.videoModal}
                id="lengthSelectorVideoModal"
                isOpen={videoModalIsOpen}
                handleClose={videoModalCloseHandler}>
                <StyledWrapper>
                    <VideoPlayer
                        videoUrl={videoLink?.url || ''}
                        videoCaptionUrl={captionLink?.url || ''}
                    />
                </StyledWrapper>
            </Modal>
        </LoadingOverlay>
    )
};

export default LengthSelectorScreen;